import * as React from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Button,
  Typography,
  Divider,
  Card,
  Row,
  Col,
  DatePicker,
  Space,
  Tag,
  Table,
} from "antd";
import dayjs from "dayjs";
import { PlusOutlined, DeleteOutlined, SelectOutlined, SearchOutlined } from "@ant-design/icons";
import AppInput from "src/components/common/AppInput";
import AppInputNumber from "src/components/common/AppInputNumber";
import {
  getCampaignApi,
  createCampaignApi,
  updateCampaignApi,
  type CampaignListItem,
} from "src/api/campaign/campaignApi";
import type {
  CampaignRuleInput,
  TriggerType,
  AttendanceType,
  ConditionType,
} from "src/types/campaign";
import { snackbarStore } from "src/store/snackbarStore";
import styled from "styled-components";
import RewardSelectModal from "./RewardSelectModal";
import ProductSelectModal from "src/components/reward/ProductSelectModal";
import type { RewardListItem } from "src/api/reward/rewardApi";
import type { ProductListItem } from "src/api/product/productApi";
import type { BrandListItem } from "src/api/brand/brandApi";
import type { CollectionNode } from "src/types/collection/collectionTypes";

type Props = {
  open: boolean;
  editingItem: CampaignListItem | null;
  onClose: () => void;
  onSuccess: () => void;
};

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

// 캠페인 triggerType 별로 규칙 카드에서 노출할 설정 그룹이 달라집니다.
const TRIGGER_TYPE_OPTIONS = [
  { value: "AUTO", label: "자동 발급" },
  { value: "MANUAL", label: "수동 발급" },
  { value: "CLAIM", label: "직접 수령 (CLAIM)" },
  { value: "EVENT_ATTENDANCE", label: "출석체크 이벤트" },
  { value: "EVENT_RANDOM", label: "확률 기반 이벤트" },
];

const DEFAULT_RULE: CampaignRuleInput = {
  rewardId: null,
  conditionType: "SIGNUP",
  conditionValue: 0,
  conditionPeriodDays: 0,
  conditionProductIds: "",
  conditionUrl: "",
  targetProductIds: "",
  targetCollectionIds: "",
  targetBrandIds: "",
  requiredCount: 0,
  winRate: 0,
  slotIndex: 0,
  issueName: "",
  isNone: false,
  priority: 0,
  isActive: true,
};

export default function CampaignUpsertModal({ open, editingItem, onClose, onSuccess }: Props) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [rules, setRules] = React.useState<CampaignRuleInput[]>([]);
  const [triggerType, setTriggerType] = React.useState<TriggerType>("AUTO");
  const isAlwaysOn = Form.useWatch("isAlwaysOn", form);

  // 모달 상태
  const [rewardModalOpen, setRewardModalOpen] = React.useState(false);
  const [productModalOpen, setProductModalOpen] = React.useState(false);
  const [collectionModalOpen, setCollectionModalOpen] = React.useState(false);
  const [brandModalOpen, setBrandModalOpen] = React.useState(false);
  const [currentRuleIndex, setCurrentRuleIndex] = React.useState<number | null>(null);

  // 선택된 리워드/상품 정보 저장
  const [selectedRewards, setSelectedRewards] = React.useState<
    Record<number, { id: number; name: string }>
  >({});
  const [selectedProducts, setSelectedProducts] = React.useState<Record<number, ProductListItem[]>>(
    {}
  );
  const [selectedCollections, setSelectedCollections] = React.useState<
    Record<number, CollectionNode[]>
  >({});
  const [selectedBrands, setSelectedBrands] = React.useState<Record<number, BrandListItem[]>>({});

  const isEdit = !!editingItem;

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      setRules([]);
      setTriggerType("AUTO");
      setSelectedRewards({});
      setSelectedProducts({});
      setSelectedCollections({});
      setSelectedBrands({});
      return;
    }

    if (isEdit) {
      const fetchDetail = async () => {
        try {
          setLoading(true);
          const res = await getCampaignApi(editingItem.campaignId);
          if (res.statusCode !== 1 || !res.data) {
            toast(res.message || "캠페인 정보를 불러올 수 없습니다.", "error");
            onClose();
            return;
          }

          const data = res.data;
          form.setFieldsValue({
            triggerType: data.triggerType,
            displayType: data.displayType,
            campaignName: data.campaignName,
            description: data.description,
            attendanceType: data.attendanceType,
            targetCount: data.targetCount,
            dailyLimit: data.dailyLimit,
            totalLimit: data.totalLimit,
            isAlwaysOn: data.isAlwaysOn,
            campaignStartAt: data.campaignStartAt ? dayjs(data.campaignStartAt) : null,
            campaignEndAt: data.campaignEndAt ? dayjs(data.campaignEndAt) : null,
          });
          setTriggerType(data.triggerType);

          const rulesData = (data.rules || []).map((r) => ({
            ruleId: r.ruleId,
            rewardId: r.isNone ? null : r.rewardId,
            conditionType: r.conditionType,
            conditionValue: r.conditionValue,
            conditionPeriodDays: r.conditionPeriodDays,
            conditionProductIds: r.conditionProductIds,
            conditionUrl: r.conditionUrl,
            conditionStartAt: r.conditionStartAt,
            conditionEndAt: r.conditionEndAt,
            targetProductIds: r.targetProducts?.map((p) => p.productId).join(",") || "",
            targetCollectionIds: r.targetCollections?.map((c) => c.collectionId).join(",") || "",
            targetBrandIds: r.targetBrands?.map((b) => b.brandId).join(",") || "",
            requiredCount: r.requiredCount,
            winRate: r.winRate,
            slotIndex: r.slotIndex,
            issueName: r.issueName,
            isNone: r.isNone,
            priority: r.priority,
            isActive: r.isActive,
          }));

          setRules(rulesData);

          // 선택된 리워드/상품 정보 복원
          const rewardsMap: Record<number, { id: number; name: string }> = {};
          const productsMap: Record<number, ProductListItem[]> = {};
          const collectionsMap: Record<number, CollectionNode[]> = {};
          const brandsMap: Record<number, BrandListItem[]> = {};

          data.rules?.forEach((r, index) => {
            if (r.isNone) return;

            if (r.rewardId && r.rewardName) {
              rewardsMap[index] = { id: r.rewardId, name: r.rewardName };
            }

            // targetProducts 복원
            if (r.targetProducts && r.targetProducts.length > 0) {
              productsMap[index] = r.targetProducts.map((p) => ({
                productId: p.productId,
                productName: p.productName,
              })) as ProductListItem[];
            }

            // targetCollections 복원
            if (r.targetCollections && r.targetCollections.length > 0) {
              collectionsMap[index] = r.targetCollections.map((c) => ({
                collectionId: c.collectionId,
                collectionName: c.collectionName,
                collectionCode: "",
                depth: 0,
                isActive: true,
              })) as CollectionNode[];
            }

            // targetBrands 복원
            if (r.targetBrands && r.targetBrands.length > 0) {
              brandsMap[index] = r.targetBrands.map((b) => ({
                id: b.brandId,
                brandNameKo: b.brandName,
                brandNameEn: "",
                brandCode: "",
                isActive: true,
                displayOrder: 0,
              })) as BrandListItem[];
            }
          });

          setSelectedRewards(rewardsMap);
          setSelectedProducts(productsMap);
          setSelectedCollections(collectionsMap);
          setSelectedBrands(brandsMap);
        } catch (e: any) {
          toast(e?.message || "캠페인 정보를 불러오는 중 오류가 발생했습니다.", "error");
          onClose();
        } finally {
          setLoading(false);
        }
      };
      fetchDetail();
    } else {
      form.setFieldsValue({
        triggerType: "AUTO",
        displayType: "ROULETTE",
        attendanceType: "MONTHLY",
        targetCount: 0,
        dailyLimit: 1,
        totalLimit: 0,
        isAlwaysOn: false,
      });
    }
  }, [open, isEdit, editingItem, form, onClose]);

  const handleTriggerTypeChange = (value: TriggerType) => {
    setTriggerType(value);
  };

  const handleAddRule = () => {
    setRules([...rules, { ...DEFAULT_RULE, priority: rules.length }]);
  };

  const handleRemoveRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const handleRuleChange = (index: number, field: keyof CampaignRuleInput, value: any) => {
    setRules((prev) =>
      prev.map((rule, i) => {
        if (i !== index) return rule;
        const updated = { ...rule, [field]: value };
        if (field === "isNone" && value === true) {
          updated.rewardId = null;
        }
        return updated;
      })
    );
  };

  // 리워드 선택 모달 열기
  const handleOpenRewardSelectionModal = (index: number) => {
    setCurrentRuleIndex(index);
    setRewardModalOpen(true);
  };

  // 리워드 선택 완료
  const handleRewardSelect = (reward: RewardListItem) => {
    if (currentRuleIndex === null) return;

    handleRuleChange(currentRuleIndex, "rewardId", reward.rewardId);
    setSelectedRewards((prev) => ({
      ...prev,
      [currentRuleIndex]: { id: reward.rewardId, name: reward.rewardName },
    }));
    setRewardModalOpen(false);
    setCurrentRuleIndex(null);
  };

  // 상품 선택 모달 열기
  const handleOpenProductModal = (index: number) => {
    setCurrentRuleIndex(index);
    setProductModalOpen(true);
  };

  // 상품 선택 완료
  const handleProductConfirm = (products: ProductListItem[]) => {
    if (currentRuleIndex === null) return;

    const productIds = products.map((p) => p.productId).join(",");
    // AUTO 캠페인은 조건(conditionProductIds), 그 외에는 대상(targetProductIds) 으로 저장합니다.
    const fieldName = triggerType === "AUTO" ? "conditionProductIds" : "targetProductIds";
    handleRuleChange(currentRuleIndex, fieldName, productIds);
    setSelectedProducts((prev) => ({
      ...prev,
      [currentRuleIndex]: products,
    }));
    setProductModalOpen(false);
    setCurrentRuleIndex(null);
  };

  // 콜렉션 선택 모달 열기
  const handleOpenCollectionModal = (index: number) => {
    setCurrentRuleIndex(index);
    setCollectionModalOpen(true);
  };

  // 콜렉션 선택 완료
  const handleCollectionConfirm = (collections: CollectionNode[]) => {
    if (currentRuleIndex === null) return;

    const collectionIds = collections.map((c) => c.collectionId).join(",");
    handleRuleChange(currentRuleIndex, "targetCollectionIds", collectionIds);
    setSelectedCollections((prev) => ({
      ...prev,
      [currentRuleIndex]: collections,
    }));
    setCollectionModalOpen(false);
    setCurrentRuleIndex(null);
  };

  // 브랜드 선택 모달 열기
  const handleOpenBrandModal = (index: number) => {
    setCurrentRuleIndex(index);
    setBrandModalOpen(true);
  };

  // 브랜드 선택 완료
  const handleBrandConfirm = (brands: BrandListItem[]) => {
    if (currentRuleIndex === null) return;

    const brandIds = brands.map((b) => b.id).join(",");
    handleRuleChange(currentRuleIndex, "targetBrandIds", brandIds);
    setSelectedBrands((prev) => ({
      ...prev,
      [currentRuleIndex]: brands,
    }));
    setBrandModalOpen(false);
    setCurrentRuleIndex(null);
  };

  const handleSubmit = async (values: any) => {
    if (triggerType !== "MANUAL" && rules.length === 0) {
      toast("최소 1개 이상의 규칙을 추가해야 합니다.", "warning");
      return;
    }

    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      if (!rule.isNone && !rule.rewardId) {
        toast(`규칙 ${i + 1}: 당첨 슬롯은 리워드가 필요합니다.`, "warning");
        return;
      }
    }

    if (triggerType === "EVENT_RANDOM") {
      const activeRules = rules.filter((r) => r.isActive);
      const totalWinRate = activeRules.reduce((sum, r) => sum + (r.winRate || 0), 0);
      if (totalWinRate !== 100) {
        toast("활성 슬롯의 당첨률 합계는 100%여야 합니다.", "warning");
        return;
      }
    }

    try {
      setSubmitting(true);

      const payload = {
        triggerType: values.triggerType,
        displayType: values.displayType,
        campaignName: values.campaignName,
        description: values.description || "",
        attendanceType: values.attendanceType as AttendanceType,
        targetCount: values.targetCount ?? 0,
        dailyLimit: values.dailyLimit ?? 1,
        totalLimit: values.totalLimit ?? 0,
        isAlwaysOn: values.isAlwaysOn ?? false,
        campaignStartAt: values.campaignStartAt
          ? (values.campaignStartAt as dayjs.Dayjs).toISOString()
          : "",
        campaignEndAt: values.campaignEndAt
          ? (values.campaignEndAt as dayjs.Dayjs).toISOString()
          : "",
        rules: rules.map((rule) => ({
          ...(rule.ruleId ? { ruleId: rule.ruleId } : {}),
          ...(rule.rewardId ? { rewardId: rule.rewardId } : {}),
          conditionType: rule.conditionType,
          conditionValue: rule.conditionValue ?? 0,
          conditionPeriodDays: rule.conditionPeriodDays ?? 0,
          conditionProductIds: rule.conditionProductIds ?? "",
          conditionUrl: rule.conditionUrl ?? "",
          conditionStartAt: rule.conditionStartAt ?? "",
          conditionEndAt: rule.conditionEndAt ?? "",
          targetProductIds: rule.targetProductIds ?? "",
          targetCollectionIds: rule.targetCollectionIds ?? "",
          targetBrandIds: rule.targetBrandIds ?? "",
          requiredCount: rule.requiredCount ?? 0,
          winRate: rule.winRate ?? 0,
          slotIndex: rule.slotIndex ?? 0,
          issueName: rule.issueName ?? "",
          isNone: rule.isNone ?? false,
          priority: rule.priority ?? 0,
          isActive: rule.isActive ?? true,
        })),
      };

      let res;
      if (isEdit) {
        res = await updateCampaignApi(editingItem.campaignId, payload);
      } else {
        res = await createCampaignApi(payload);
      }

      if (res.statusCode === 1) {
        toast(isEdit ? "캠페인이 수정되었습니다." : "캠페인이 등록되었습니다.", "success");
        onSuccess();
      } else {
        toast(res?.message || `캠페인 ${isEdit ? "수정" : "등록"} 실패`, "error");
      }
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const renderRuleCard = (rule: CampaignRuleInput, index: number) => {
    return (
      <StyledRuleCard
        key={index}
        size="small"
        title={
          <Space>
            <Typography.Text strong>{`규칙 ${index + 1}`}</Typography.Text>
            <Switch
              checked={rule.isNone}
              onChange={(val) => handleRuleChange(index, "isNone", val)}
              checkedChildren="꽝"
              unCheckedChildren="당첨"
              size="small"
            />
          </Space>
        }
        extra={
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveRule(index)}
          >
            삭제
          </Button>
        }
      >
        {/* 리워드 정보 */}
        {!rule.isNone && (
          <>
            <SectionTitle>리워드 정보</SectionTitle>
            <Row gutter={[12, 12]}>
              <Col span={24}>
                <FieldLabel>
                  리워드 선택 <RequiredMark>*</RequiredMark>
                </FieldLabel>
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    value={
                      selectedRewards[index]
                        ? `[${selectedRewards[index].id}] ${selectedRewards[index].name}`
                        : rule.rewardId
                          ? `ID: ${rule.rewardId}`
                          : ""
                    }
                    placeholder="리워드 선택 (쿠폰팩은 리워드의 보상 항목으로 구성합니다)"
                    readOnly
                  />
                  <Button
                    icon={<SelectOutlined />}
                    onClick={() => handleOpenRewardSelectionModal(index)}
                  >
                    선택
                  </Button>
                </Space.Compact>
              </Col>
            </Row>
            <SectionDivider />
          </>
        )}

        {/* 이벤트 캠페인 전용 기본 설정 */}
        {(triggerType === "EVENT_ATTENDANCE" || triggerType === "EVENT_RANDOM") && (
          <>
            <SectionTitle>규칙 기본 설정</SectionTitle>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <FieldLabel>지급명</FieldLabel>
                <Input
                  value={rule.issueName}
                  onChange={(e) => handleRuleChange(index, "issueName", e.target.value)}
                  placeholder="예: 3,000P"
                />
              </Col>
              <Col span={12}>
                <FieldLabel>슬롯 번호</FieldLabel>
                <InputNumber
                  value={rule.slotIndex}
                  onChange={(val) => handleRuleChange(index, "slotIndex", val ?? 0)}
                  style={{ width: "100%" }}
                  placeholder="슬롯"
                  min={0}
                />
              </Col>
            </Row>
          </>
        )}

        {/* AUTO 캠페인: 자동 발급 조건 설정 */}
        {triggerType === "AUTO" && (
          <>
            <SectionDivider />
            <SectionTitle>자동 발급 조건</SectionTitle>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <FieldLabel>조건 타입</FieldLabel>
                <Select
                  value={rule.conditionType}
                  onChange={(val: ConditionType) => handleRuleChange(index, "conditionType", val)}
                  style={{ width: "100%" }}
                  options={[
                    { value: "SIGNUP", label: "회원가입 완료 시 발급" },
                    { value: "ORDER", label: "주문 완료 시 발급 (최소 주문 금액)" },
                    { value: "REVIEW", label: "리뷰 작성 완료 시 발급" },
                    { value: "PET_INFO", label: "반려동물 정보 등록 시 발급" },
                    { value: "VISIT_STAY", label: "특정 페이지 체류 시 발급" },
                    // { value: "PRODUCT_PURCHASE", label: "특정 상품 구매 시 발급" },
                    // { value: "CART", label: "장바구니 담기 시 발급 (최소 금액)" },
                    // { value: "MIN_AMOUNT", label: "누적 구매액 달성 시 발급" },
                    // { value: "PERIOD_PURCHASE", label: "특정 기간 내 구매액 달성 시 발급" },
                    // { value: "FIRST_ORDER_CONFIRM", label: "첫 구매 확정" },
                  ]}
                />
              </Col>

              {/* ORDER, CART, MIN_AMOUNT: conditionValue 필요 (금액) */}
              {(rule.conditionType === "ORDER" ||
                rule.conditionType === "CART" ||
                rule.conditionType === "MIN_AMOUNT") && (
                <Col span={12}>
                  <FieldLabel>
                    {rule.conditionType === "ORDER"
                      ? "최소 주문 금액 (원, 0이면 금액 무관)"
                      : rule.conditionType === "CART"
                        ? "최소 금액 (원, 0이면 금액 무관)"
                        : "목표 금액 (원)"}
                  </FieldLabel>
                  <InputNumber
                    value={rule.conditionValue}
                    onChange={(val) => handleRuleChange(index, "conditionValue", val ?? 0)}
                    style={{ width: "100%" }}
                    placeholder="금액 입력"
                    min={0}
                  />
                </Col>
              )}

              {/* VISIT_STAY: conditionValue (시간), conditionUrl 필요 */}
              {rule.conditionType === "VISIT_STAY" && (
                <>
                  <Col span={12}>
                    <FieldLabel>체류 시간 (초)</FieldLabel>
                    <InputNumber
                      value={rule.conditionValue}
                      onChange={(val) => handleRuleChange(index, "conditionValue", val ?? 0)}
                      style={{ width: "100%" }}
                      placeholder="체류 시간"
                      min={0}
                    />
                  </Col>
                  <Col span={12}>
                    <FieldLabel>대상 URL</FieldLabel>
                    <Input
                      value={rule.conditionUrl}
                      onChange={(e) => handleRuleChange(index, "conditionUrl", e.target.value)}
                      placeholder="대상 URL"
                    />
                  </Col>
                </>
              )}

              {/* PRODUCT_PURCHASE: conditionProductIds 필요 */}
              {rule.conditionType === "PRODUCT_PURCHASE" && (
                <Col span={12}>
                  <FieldLabel>대상 상품</FieldLabel>
                  <Space direction="vertical" style={{ width: "100%" }} size={8}>
                    <Button
                      icon={<SelectOutlined />}
                      onClick={() => handleOpenProductModal(index)}
                      style={{ width: "100%" }}
                    >
                      상품 선택 ({selectedProducts[index]?.length || 0}개)
                    </Button>
                    {selectedProducts[index] && selectedProducts[index].length > 0 && (
                      <Space wrap>
                        {selectedProducts[index].map((product) => (
                          <Tag
                            key={product.productId}
                            closable
                            onClose={() => {
                              const filtered = selectedProducts[index].filter(
                                (p) => p.productId !== product.productId
                              );
                              const productIds = filtered.map((p) => p.productId).join(",");
                              handleRuleChange(index, "conditionProductIds", productIds);
                              setSelectedProducts((prev) => ({
                                ...prev,
                                [index]: filtered,
                              }));
                            }}
                          >
                            [{product.productId}] {product.productName}
                          </Tag>
                        ))}
                      </Space>
                    )}
                  </Space>
                </Col>
              )}

              {/* PERIOD_PURCHASE: conditionValue (금액), conditionPeriodDays 필요 */}
              {rule.conditionType === "PERIOD_PURCHASE" && (
                <>
                  <Col span={12}>
                    <FieldLabel>목표 금액 (원)</FieldLabel>
                    <InputNumber
                      value={rule.conditionValue}
                      onChange={(val) => handleRuleChange(index, "conditionValue", val ?? 0)}
                      style={{ width: "100%" }}
                      placeholder="목표 금액"
                      min={0}
                    />
                  </Col>
                  <Col span={12}>
                    <FieldLabel>기간 (일)</FieldLabel>
                    <InputNumber
                      value={rule.conditionPeriodDays}
                      onChange={(val) => handleRuleChange(index, "conditionPeriodDays", val ?? 0)}
                      style={{ width: "100%" }}
                      placeholder="기간"
                      min={0}
                    />
                  </Col>
                </>
              )}

              {/* 조건 적용 기간 */}
              <Col span={12}>
                <FieldLabel>조건 적용 시작일</FieldLabel>
                <DatePicker
                  showTime
                  value={rule.conditionStartAt ? dayjs(rule.conditionStartAt) : null}
                  onChange={(date) =>
                    handleRuleChange(index, "conditionStartAt", date ? date.toISOString() : "")
                  }
                  style={{ width: "100%" }}
                  placeholder="시작일 선택 (선택사항)"
                  format="YYYY-MM-DD HH:mm:ss"
                />
              </Col>
              <Col span={12}>
                <FieldLabel>조건 적용 종료일</FieldLabel>
                <DatePicker
                  showTime
                  value={rule.conditionEndAt ? dayjs(rule.conditionEndAt) : null}
                  onChange={(date) =>
                    handleRuleChange(index, "conditionEndAt", date ? date.toISOString() : "")
                  }
                  style={{ width: "100%" }}
                  placeholder="종료일 선택 (선택사항)"
                  format="YYYY-MM-DD HH:mm:ss"
                />
              </Col>
            </Row>
          </>
        )}

        {/* MANUAL/CLAIM 캠페인: 대상 설정 */}
        {(triggerType === "MANUAL" || triggerType === "CLAIM") && (
          <>
            <SectionDivider />
            <SectionTitle>대상 설정 (상품/콜렉션/브랜드)</SectionTitle>
            <Row gutter={[12, 12]}>
              <Col span={8}>
                <FieldLabel>대상 상품</FieldLabel>
                <Button
                  icon={<SelectOutlined />}
                  onClick={() => handleOpenProductModal(index)}
                  style={{ width: "100%" }}
                >
                  상품 선택 ({selectedProducts[index]?.length || 0}개)
                </Button>
              </Col>
              <Col span={8}>
                <FieldLabel>대상 콜렉션</FieldLabel>
                <Button
                  icon={<SelectOutlined />}
                  onClick={() => handleOpenCollectionModal(index)}
                  style={{ width: "100%" }}
                >
                  콜렉션 선택 ({selectedCollections[index]?.length || 0}개)
                </Button>
              </Col>
              <Col span={8}>
                <FieldLabel>대상 브랜드</FieldLabel>
                <Button
                  icon={<SelectOutlined />}
                  onClick={() => handleOpenBrandModal(index)}
                  style={{ width: "100%" }}
                >
                  브랜드 선택 ({selectedBrands[index]?.length || 0}개)
                </Button>
              </Col>
            </Row>
            {/* 선택된 항목들을 각 줄로 표시 */}
            {selectedProducts[index] && selectedProducts[index].length > 0 && (
              <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                <Col span={24}>
                  <FieldLabel>선택된 상품</FieldLabel>
                  <Space wrap>
                    {selectedProducts[index].map((product) => (
                      <Tag
                        key={product.productId}
                        closable
                        onClose={() => {
                          const filtered = selectedProducts[index].filter(
                            (p) => p.productId !== product.productId
                          );
                          const productIds = filtered.map((p) => p.productId).join(",");
                          handleRuleChange(index, "targetProductIds", productIds);
                          setSelectedProducts((prev) => ({
                            ...prev,
                            [index]: filtered,
                          }));
                        }}
                      >
                        [{product.productId}] {product.productName}
                      </Tag>
                    ))}
                  </Space>
                </Col>
              </Row>
            )}
            {selectedCollections[index] && selectedCollections[index].length > 0 && (
              <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                <Col span={24}>
                  <FieldLabel>선택된 콜렉션</FieldLabel>
                  <Space wrap>
                    {selectedCollections[index].map((collection) => (
                      <Tag
                        key={collection.collectionId}
                        closable
                        color="blue"
                        onClose={() => {
                          const filtered = selectedCollections[index].filter(
                            (c) => c.collectionId !== collection.collectionId
                          );
                          const collectionIds = filtered.map((c) => c.collectionId).join(",");
                          handleRuleChange(index, "targetCollectionIds", collectionIds);
                          setSelectedCollections((prev) => ({
                            ...prev,
                            [index]: filtered,
                          }));
                        }}
                      >
                        [{collection.collectionId}] {collection.collectionName}
                      </Tag>
                    ))}
                  </Space>
                </Col>
              </Row>
            )}
            {selectedBrands[index] && selectedBrands[index].length > 0 && (
              <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                <Col span={24}>
                  <FieldLabel>선택된 브랜드</FieldLabel>
                  <Space wrap>
                    {selectedBrands[index].map((brand) => (
                      <Tag
                        key={brand.id}
                        closable
                        color="green"
                        onClose={() => {
                          const filtered = selectedBrands[index].filter((b) => b.id !== brand.id);
                          const brandIds = filtered.map((b) => b.id).join(",");
                          handleRuleChange(index, "targetBrandIds", brandIds);
                          setSelectedBrands((prev) => ({
                            ...prev,
                            [index]: filtered,
                          }));
                        }}
                      >
                        [{brand.id}] {brand.brandNameKo}
                      </Tag>
                    ))}
                  </Space>
                </Col>
              </Row>
            )}
          </>
        )}

        {/* 출석체크 캠페인: 출석횟수 설정 */}
        {triggerType === "EVENT_ATTENDANCE" && (
          <>
            <SectionDivider />
            <SectionTitle>출석횟수 설정</SectionTitle>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <FieldLabel>필요 출석 횟수</FieldLabel>
                <InputNumber
                  value={rule.requiredCount}
                  onChange={(val) => handleRuleChange(index, "requiredCount", val ?? 0)}
                  style={{ width: "100%" }}
                  placeholder="횟수"
                  min={0}
                />
              </Col>
            </Row>
          </>
        )}

        {/* 확률 기반 캠페인: 확률 설정 */}
        {triggerType === "EVENT_RANDOM" && (
          <>
            <SectionDivider />
            <SectionTitle>확률 설정</SectionTitle>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <FieldLabel>
                  당첨률 (%) <RequiredMark>*</RequiredMark>
                </FieldLabel>
                <InputNumber
                  value={rule.winRate}
                  onChange={(val) => handleRuleChange(index, "winRate", val ?? 0)}
                  style={{ width: "100%" }}
                  placeholder="당첨률"
                  min={0}
                  max={100}
                />
              </Col>
            </Row>
          </>
        )}

        {/* 공통 설정 */}
        <SectionDivider />
        <SectionTitle>기타 설정</SectionTitle>
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <FieldLabel>우선순위</FieldLabel>
            <InputNumber
              value={rule.priority}
              onChange={(val) => handleRuleChange(index, "priority", val ?? 0)}
              style={{ width: "100%" }}
              min={0}
              placeholder="우선순위"
            />
          </Col>
          <Col span={12}>
            <FieldLabel>활성 상태</FieldLabel>
            <div>
              <Switch
                checked={rule.isActive}
                onChange={(val) => handleRuleChange(index, "isActive", val)}
                checkedChildren="활성"
                unCheckedChildren="비활성"
              />
            </div>
          </Col>
        </Row>
      </StyledRuleCard>
    );
  };

  return (
    <Modal
      title={isEdit ? "캠페인 수정" : "캠페인 등록"}
      open={open}
      onCancel={onClose}
      maskClosable={false}
      width={1200}
      styles={{
        body: {
          maxHeight: "70vh",
          overflowY: "auto",
          padding: "24px",
        },
      }}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={submitting}>
          취소
        </Button>,
        <Button key="submit" type="primary" loading={submitting} onClick={() => form.submit()}>
          {isEdit ? "수정" : "등록"}
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>로딩 중...</div>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* 기본 정보 */}
          <FormSection>
            <SectionHeader level={5}>기본 정보</SectionHeader>
            <Row gutter={16}>
              <Col span={triggerType === "EVENT_RANDOM" ? 12 : 18}>
                <Form.Item
                  label="캠페인명"
                  name="campaignName"
                  rules={[{ required: true, message: "캠페인명을 입력하세요" }]}
                >
                  <AppInput placeholder="캠페인명 입력" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="트리거 타입" name="triggerType" rules={[{ required: true }]}>
                  <Select onChange={handleTriggerTypeChange} options={TRIGGER_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
              {triggerType === "EVENT_RANDOM" && (
                <Col span={6}>
                  <Form.Item label="표시 타입" name="displayType">
                    <Select
                      options={[
                        { value: "ROULETTE", label: "룰렛" },
                        { value: "LOTTERY", label: "복권" },
                        { value: "SCRATCH", label: "스크래치" },
                      ]}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Form.Item label="설명" name="description">
              <Input.TextArea rows={2} placeholder="캠페인 설명 입력" />
            </Form.Item>
          </FormSection>

          {/* 운영 설정 */}
          <FormSection>
            <SectionHeader level={5}>운영 설정</SectionHeader>
            <Row gutter={16}>
              {triggerType === "EVENT_ATTENDANCE" && (
                <Col span={6}>
                  <Form.Item label="출석 타입" name="attendanceType" rules={[{ required: true }]}>
                    <Select
                      options={[
                        { value: "MONTHLY", label: "월별" },
                        { value: "COUNT", label: "횟수 기반" },
                      ]}
                    />
                  </Form.Item>
                </Col>
              )}
              {triggerType !== "AUTO" && triggerType !== "MANUAL" && triggerType !== "CLAIM" && (
                <Col span={6}>
                  <Form.Item label="목표 횟수" name="targetCount">
                    <AppInputNumber style={{ width: "100%" }} min={0} placeholder="0" />
                  </Form.Item>
                </Col>
              )}
              <Col span={6}>
                <Form.Item label="총 제한" name="totalLimit">
                  <AppInputNumber style={{ width: "100%" }} min={0} placeholder="0 = 무제한" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="일일 제한" name="dailyLimit">
                  <AppInputNumber style={{ width: "100%" }} min={0} placeholder="1" />
                </Form.Item>
              </Col>
            </Row>
          </FormSection>

          {/* 기간 설정 */}
          <FormSection>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <SectionHeader level={5} style={{ margin: 0 }}>
                기간 설정
              </SectionHeader>
              <Form.Item name="isAlwaysOn" valuePropName="checked" style={{ margin: 0 }}>
                <Switch checkedChildren="상시" unCheckedChildren="기간제" />
              </Form.Item>
            </div>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="캠페인 시작일"
                  name="campaignStartAt"
                  rules={[
                    {
                      required: !isAlwaysOn,
                      message: "시작일을 선택하세요",
                    },
                  ]}
                >
                  <DatePicker
                    showTime
                    style={{ width: "100%" }}
                    placeholder="시작일 선택"
                    format="YYYY-MM-DD HH:mm:ss"
                    disabled={isAlwaysOn}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="캠페인 종료일"
                  name="campaignEndAt"
                  rules={[
                    {
                      required: !isAlwaysOn,
                      message: "종료일을 선택하세요",
                    },
                  ]}
                >
                  <DatePicker
                    showTime
                    style={{ width: "100%" }}
                    placeholder="종료일 선택"
                    format="YYYY-MM-DD HH:mm:ss"
                    disabled={isAlwaysOn}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSection>

          <Divider />

          {/* 규칙 관리 */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <SectionHeader level={5} style={{ margin: 0 }}>
              캠페인 규칙
            </SectionHeader>
            <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddRule}>
              규칙 추가
            </Button>
          </div>

          {rules.length === 0 ? (
            <EmptyRulesPlaceholder>규칙을 추가하세요</EmptyRulesPlaceholder>
          ) : (
            rules.map((rule, index) => renderRuleCard(rule, index))
          )}
        </Form>
      )}

      {/* 리워드 선택 모달 */}
      <RewardSelectModal
        open={rewardModalOpen}
        onClose={() => {
          setRewardModalOpen(false);
          setCurrentRuleIndex(null);
        }}
        onSelect={handleRewardSelect}
      />

      {/* 상품 선택 모달 */}
      <ProductSelectModal
        open={productModalOpen}
        onClose={() => {
          setProductModalOpen(false);
          setCurrentRuleIndex(null);
        }}
        onConfirm={handleProductConfirm}
        selectedProductIds={
          currentRuleIndex !== null && selectedProducts[currentRuleIndex]
            ? selectedProducts[currentRuleIndex].map((p) => p.productId)
            : []
        }
        initialSelectedProducts={
          currentRuleIndex !== null ? selectedProducts[currentRuleIndex] || [] : []
        }
      />

      {/* 콜렉션 선택 모달 */}
      <CollectionSelectModal
        open={collectionModalOpen}
        onClose={() => {
          setCollectionModalOpen(false);
          setCurrentRuleIndex(null);
        }}
        onConfirm={handleCollectionConfirm}
        initialSelectedCollections={
          currentRuleIndex !== null ? selectedCollections[currentRuleIndex] || [] : []
        }
      />

      {/* 브랜드 선택 모달 */}
      <BrandSelectModal
        open={brandModalOpen}
        onClose={() => {
          setBrandModalOpen(false);
          setCurrentRuleIndex(null);
        }}
        onConfirm={handleBrandConfirm}
        initialSelectedBrands={
          currentRuleIndex !== null ? selectedBrands[currentRuleIndex] || [] : []
        }
      />
    </Modal>
  );
}

// 콜렉션 선택 모달 컴포넌트
function CollectionSelectModal({
  open,
  onClose,
  onConfirm,
  initialSelectedCollections,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (collections: CollectionNode[]) => void;
  initialSelectedCollections: CollectionNode[];
}) {
  const [loading, setLoading] = React.useState(false);
  const [collections, setCollections] = React.useState<CollectionNode[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [searchText, setSearchText] = React.useState("");
  const [selectedRows, setSelectedRows] = React.useState<CollectionNode[]>(
    initialSelectedCollections
  );
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<number[]>(
    initialSelectedCollections.map((c) => c.collectionId)
  );

  const fetchCollections = React.useCallback(async () => {
    try {
      setLoading(true);
      const { getCollectionHierarchyApi } = await import("src/api/collection/collectionApi");
      const res = await getCollectionHierarchyApi({
        page,
        size: pageSize,
        order: "displayOrder,asc",
        collectionName: searchText || undefined,
      });

      if (res.statusCode === 1 && res.data) {
        setCollections(res.data.content);
        setTotal(res.data.page.totalElements);
      } else {
        snackbarStore.getState().addSnackbar({
          message: res.message || "콜렉션 목록을 불러올 수 없습니다.",
          severity: "error",
        });
      }
    } catch (e: any) {
      snackbarStore.getState().addSnackbar({
        message: e?.message || "콜렉션 목록을 불러오는 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchText]);

  React.useEffect(() => {
    if (open) {
      setSelectedRows(initialSelectedCollections);
      setSelectedRowKeys(initialSelectedCollections.map((c) => c.collectionId));
      fetchCollections();
    } else {
      setSearchText("");
      setPage(0);
    }
  }, [open, fetchCollections, initialSelectedCollections]);

  const handleConfirm = () => {
    onConfirm(selectedRows);
    onClose();
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[], selectedRowsData: CollectionNode[]) => {
      setSelectedRowKeys(selectedKeys as number[]);

      const previouslySelected = selectedRows.filter(
        (c) => !collections.find((coll) => coll.collectionId === c.collectionId)
      );
      const newSelectedRows = [...previouslySelected, ...selectedRowsData];

      const uniqueSelectedRows = newSelectedRows.filter(
        (collection, index, self) =>
          selectedKeys.includes(collection.collectionId) &&
          index === self.findIndex((c) => c.collectionId === collection.collectionId)
      );

      setSelectedRows(uniqueSelectedRows);
    },
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "collectionId",
      width: 80,
    },
    {
      title: "콜렉션명",
      dataIndex: "collectionName",
      width: 250,
    },
    {
      title: "콜렉션 코드",
      dataIndex: "collectionCode",
      width: 150,
    },
    {
      title: "Depth",
      dataIndex: "depth",
      width: 80,
    },
    {
      title: "활성 상태",
      dataIndex: "isActive",
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "default"}>{isActive ? "활성" : "비활성"}</Tag>
      ),
    },
  ];

  return (
    <Modal
      title="콜렉션 선택 (다중 선택 가능)"
      open={open}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          선택 완료 ({selectedRowKeys.length}개)
        </Button>,
      ]}
      styles={{
        body: {
          maxHeight: "70vh",
          overflowY: "auto",
        },
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="콜렉션명으로 검색"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={fetchCollections}
          allowClear
        />
      </div>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={collections}
        rowKey="collectionId"
        loading={loading}
        pagination={{
          current: page + 1,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p - 1);
            setPageSize(ps);
          },
          showSizeChanger: true,
          showTotal: (total) => `총 ${total}개`,
        }}
        size="small"
        scroll={{ x: 1000 }}
      />
    </Modal>
  );
}

// 브랜드 선택 모달 컴포넌트
function BrandSelectModal({
  open,
  onClose,
  onConfirm,
  initialSelectedBrands,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (brands: BrandListItem[]) => void;
  initialSelectedBrands: BrandListItem[];
}) {
  const [loading, setLoading] = React.useState(false);
  const [brands, setBrands] = React.useState<BrandListItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [searchText, setSearchText] = React.useState("");
  const [selectedRows, setSelectedRows] = React.useState<BrandListItem[]>(initialSelectedBrands);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<number[]>(
    initialSelectedBrands.map((b) => b.id)
  );

  const fetchBrands = React.useCallback(async () => {
    try {
      setLoading(true);
      const { getBrandListApi } = await import("src/api/brand/brandApi");
      const res = await getBrandListApi({
        page,
        size: pageSize,
        order: "displayOrder,asc",
        brandName: searchText || undefined,
      });

      if (res.statusCode === 1 && res.data) {
        setBrands(res.data.content);
        setTotal(res.data.page.totalElements);
      } else {
        snackbarStore.getState().addSnackbar({
          message: res.message || "브랜드 목록을 불러올 수 없습니다.",
          severity: "error",
        });
      }
    } catch (e: any) {
      snackbarStore.getState().addSnackbar({
        message: e?.message || "브랜드 목록을 불러오는 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchText]);

  React.useEffect(() => {
    if (open) {
      setSelectedRows(initialSelectedBrands);
      setSelectedRowKeys(initialSelectedBrands.map((b) => b.id));
      fetchBrands();
    } else {
      setSearchText("");
      setPage(0);
    }
  }, [open, fetchBrands, initialSelectedBrands]);

  const handleConfirm = () => {
    onConfirm(selectedRows);
    onClose();
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[], selectedRowsData: BrandListItem[]) => {
      setSelectedRowKeys(selectedKeys as number[]);

      const previouslySelected = selectedRows.filter(
        (b) => !brands.find((brand) => brand.id === b.id)
      );
      const newSelectedRows = [...previouslySelected, ...selectedRowsData];

      const uniqueSelectedRows = newSelectedRows.filter(
        (brand, index, self) =>
          selectedKeys.includes(brand.id) && index === self.findIndex((b) => b.id === brand.id)
      );

      setSelectedRows(uniqueSelectedRows);
    },
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },
    {
      title: "브랜드명 (한글)",
      dataIndex: "brandNameKo",
      width: 200,
    },
    {
      title: "브랜드명 (영문)",
      dataIndex: "brandNameEn",
      width: 200,
      render: (name: string) => name || "-",
    },
    {
      title: "브랜드 코드",
      dataIndex: "brandCode",
      width: 120,
      render: (code: string) => code || "-",
    },
    {
      title: "활성 상태",
      dataIndex: "isActive",
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "default"}>{isActive ? "활성" : "비활성"}</Tag>
      ),
    },
  ];

  return (
    <Modal
      title="브랜드 선택 (다중 선택 가능)"
      open={open}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          선택 완료 ({selectedRowKeys.length}개)
        </Button>,
      ]}
      styles={{
        body: {
          maxHeight: "70vh",
          overflowY: "auto",
        },
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="브랜드명으로 검색"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={fetchBrands}
          allowClear
        />
      </div>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={brands}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page + 1,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p - 1);
            setPageSize(ps);
          },
          showSizeChanger: true,
          showTotal: (total) => `총 ${total}개`,
        }}
        size="small"
        scroll={{ x: 1000 }}
      />
    </Modal>
  );
}

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled(Typography.Title)`
  && {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 600;
  }
`;

const StyledRuleCard = styled(Card)`
  margin-bottom: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;

  .ant-card-head {
    background-color: #fafafa;
    border-bottom: 1px solid #e8e8e8;
  }
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;
  margin-top: 12px;

  &:first-child {
    margin-top: 0;
  }
`;

const SectionDivider = styled.div`
  height: 1px;
  background-color: #f0f0f0;
  margin: 16px 0;
`;

const FieldLabel = styled.div`
  font-size: 13px;
  color: #595959;
  margin-bottom: 6px;
`;

const RequiredMark = styled.span`
  color: #ff4d4f;
`;

const EmptyRulesPlaceholder = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #999;
  background-color: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
`;
